/**
 * Media outbox jobs as pending message rows: the caption and local previews
 * show from the moment the job is queued until the message is sent.
 */

import { mediaPayload, serializePayload, type MentionEntity, type PageTagEntity } from '../messagePayload';
import {
  conversationSendScope,
  spaceChannelSendScope,
  type PendingSend,
} from '../pendingSends';
import {
  createPendingPreviews,
  revokePendingPreviews,
  type PendingAttachmentPreview,
} from '../pendingSendPreviews';
import type { MediaOutboxJobRecord } from './mediaOutboxTypes';

/** Previews per job id; object URLs live until the job leaves the outbox. */
const previewCache = new Map<string, readonly PendingAttachmentPreview[]>();

function previewsFor(job: MediaOutboxJobRecord): readonly PendingAttachmentPreview[] {
  const cached = previewCache.get(job.id);
  if (cached) return cached;
  // Raw files are dropped once encrypted and uploaded; after a reload past that
  // point only names remain.
  const previews =
    job.attachmentBlobs.length > 0
      ? createPendingPreviews(job.attachmentBlobs, job.id)
      : (job.e2eSnapshot ?? []).map((m, i) => ({
          key: `${job.id}:${i}`,
          name: m.fileName ?? '',
          contentType: m.contentType,
        }));
  previewCache.set(job.id, previews);
  return previews;
}

function showsAsPendingRow(job: MediaOutboxJobRecord): boolean {
  if (job.editMessageId || job.messageSendCompleted) return false;
  return job.stage !== 'completed' && job.stage !== 'cancelled';
}

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Row for a job whose message is not sent yet; `null` once it is (or for edits). */
export function outboxJobToPendingSend(job: MediaOutboxJobRecord, identityId: string): PendingSend | null {
  if (!showsAsPendingRow(job)) return null;
  const payload = mediaPayload(job.caption || undefined, []);
  const mentions = parseJson<MentionEntity[]>(job.mentionsJson, []);
  const pageTags = parseJson<PageTagEntity[]>(job.pageTagsJson, []);
  if (mentions.length > 0) payload.mentions = mentions;
  if (pageTags.length > 0) payload.pageTags = pageTags;
  return {
    clientMessageId: job.clientMessageId ?? `outbox:${job.id}`,
    scope: job.spaceId ? spaceChannelSendScope(job.conversationId) : conversationSendScope(job.conversationId),
    fromIdentityId: identityId,
    plaintext: serializePayload(payload),
    createdAt: new Date(job.createdAt).toISOString(),
    ...(job.threadRootId ? { threadRootId: job.threadRootId } : {}),
    inStream: !job.threadRootId || job.alsoSendToChannel === true,
    ...(job.replyToMessageId ? { replyToMessageId: job.replyToMessageId } : {}),
    status: job.stage === 'failed' ? 'failed' : 'sending',
    ...(job.stage === 'failed' && job.errorMessage ? { error: job.errorMessage } : {}),
    previews: previewsFor(job),
    outboxJobId: job.id,
    outboxStage: job.stage,
  };
}

/** Releases preview URLs for jobs that no longer render as pending rows. */
export function releaseOutboxPreviews(jobs: readonly MediaOutboxJobRecord[]): void {
  const live = new Set(jobs.filter(showsAsPendingRow).map((j) => j.id));
  for (const [jobId, previews] of previewCache) {
    if (live.has(jobId)) continue;
    revokePendingPreviews(previews);
    previewCache.delete(jobId);
  }
}
